import {
  fetchAuthSession,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import type { AuthTokens } from '../../types';

// Treat tokens expiring within this window as expired so startup does not
// briefly expose a session that will fail on the user's first API request.
const SESSION_REFRESH_SKEW_MS = 30_000;

export { extractUserFromIdToken } from './jwtUtils';

function isAlreadyAuthenticatedError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.name === 'UserAlreadyAuthenticatedException' ||
    /already (a )?signed in|already logged in/i.test(error.message)
  );
}

function sessionToTokens(
  session: Awaited<ReturnType<typeof fetchAuthSession>>,
): AuthTokens | null {
  const idToken = session.tokens?.idToken;
  const accessToken = session.tokens?.accessToken;

  if (!idToken || !accessToken) {
    return null;
  }

  const exp = idToken.payload.exp;

  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return null;
  }

  return {
    idToken: idToken.toString(),
    accessToken: accessToken.toString(),
    expiresAt: exp * 1000,
  };
}

export async function signIn(
  email: string,
  password: string
): Promise<AuthTokens> {
  let nextStep: Awaited<ReturnType<typeof amplifySignIn>>['nextStep'];

  try {
    ({ nextStep } = await amplifySignIn({ username: email, password }));
  } catch (error) {
    if (!isAlreadyAuthenticatedError(error)) {
      throw error;
    }

    await amplifySignOut();
    ({ nextStep } = await amplifySignIn({ username: email, password }));
  }

  if (nextStep.signInStep === 'DONE') {
    const session = await fetchAuthSession();
    const tokens = sessionToTokens(session);

    if (!tokens) {
      throw new Error('Sign in succeeded but no session tokens were returned.');
    }

    return tokens;
  }

  if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    throw new Error('New password required before login can complete.');
  }

  throw new Error(
    `Additional sign-in step required before login can complete: ${nextStep.signInStep}.`,
  );
}

export async function signOut(): Promise<void> {
  await amplifySignOut();
}

export async function getCurrentSession(): Promise<AuthTokens | null> {
  try {
    const session = await fetchAuthSession();
    const tokens = sessionToTokens(session);

    if (tokens && tokens.expiresAt > Date.now() + SESSION_REFRESH_SKEW_MS) {
      return tokens;
    }

    // Amplify refreshes automatically in many cases, but can still return
    // cached expired tokens. Force refresh before AuthProvider marks the user
    // authenticated; if the refresh token is expired, fail closed to login.
    const refreshedSession = await fetchAuthSession({ forceRefresh: true });
    const refreshedTokens = sessionToTokens(refreshedSession);

    return refreshedTokens && refreshedTokens.expiresAt > Date.now()
      ? refreshedTokens
      : null;
  } catch {
    return null;
  }
}

export async function forgotPassword(email: string): Promise<void> {
  await resetPassword({ username: email });
}

export async function confirmPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
}
